import routes from '@/constants/routes';
import cookieKeys from '@/constants/cookie-keys';
import { defaultCurrencyId } from '@/constants/currency';

import { accountLoginAdapter, accountAdapter } from '@/adapters/account';

const utmExceptionRoutes = [routes.SEARCH, routes.CART, routes.MY_TICKETS];

export const useAuthStore = defineStore({
  id: 'auth',
  state: () => ({
    token: null,
    countryId: null,
    account: null,
    utmData: undefined,
  }),
  getters: {
    isGuest() {
      return this.account?.isGuest;
    },
  },
  actions: {
    setAuthData(
      { token, countryId },
      setToCookie = true,
      { tokenCookie, countryIdCookie } = {},
    ) {
      this.token = token;
      this.countryId = countryId;

      if (setToCookie) {
        if (!tokenCookie) tokenCookie = useCookie(cookieKeys.TOKEN);
        if (tokenCookie.value !== token) {
          tokenCookie.value = token;
        }

        if (!countryIdCookie)
          countryIdCookie = useCookie(cookieKeys.COUNTRY_ID);
        if (countryIdCookie.value !== countryId) {
          countryIdCookie.value = countryId;
        }
      }
    },

    fetchGuestToken({
      tokenCookie,
      countryIdCookie,
      currencyIdCookie,
      userDevice = undefined,
    } = {}) {
      const device = useDevice();
      const deviceData = userDevice ? userDevice : device.custom;
      const analytics = useGoogleAnalytics();
      const currencyStore = useCurrencyStore();

      if (!this.utmData) this.setUtmData();

      return useRequest('/accounts/login', {
        query: {
          ga_client_id: analytics.getGaClientId(),
          ...deviceData,
          tracking_parameters: this.utmData,
        },
      }).then((data) => {
        const { token, countryId, currencyId } = accountLoginAdapter(data);

        currencyStore.setCurrencyId(
          currencyId ?? defaultCurrencyId,
          currencyIdCookie,
        );

        this.setAuthData({ token, countryId }, true, {
          tokenCookie,
          countryIdCookie,
        });

        return this.fetchAccount();
      });
    },

    requestLoginCode(email) {
      return useRequest('/accounts/code', {
        method: 'post',
        body: { email },
      });
    },
    login({ email, code }) {
      return useRequest('/accounts/login', {
        method: 'post',
        body: { email, code },
      }).then((data) => {
        const { token, countryId } = accountLoginAdapter(data);

        this.setAuthData({ token, countryId }, true);

        return this.fetchAccount();
      });
    },
    mergeGuestAndUser({ email, code }) {
      const device = useDevice();
      const analytics = useGoogleAnalytics();

      if (!this.utmData) this.setUtmData();

      return useRequest('/accounts/merge', {
        method: 'post',
        body: {
          email,
          code,
          ...device.custom,
          ga_client_id: analytics.getGaClientId(),
          tracking_parameters: this.utmData,
        },
      }).then((data) => {
        const { token, countryId } = accountLoginAdapter(data);

        this.setAuthData({ token, countryId }, true);

        return this.fetchAccount();
      });
    },

    fetchAccount() {
      return useRequest('/accounts').then((data) => {
        this.account = accountAdapter(data);
      });
    },

    updateSessionInformation() {
      const device = useDevice();
      const analytics = useGoogleAnalytics();

      if (!this.utmData) this.setUtmData();

      return useRequest('/accounts', {
        method: 'post',
        body: {
          ...device.custom,
          ga_client_id: analytics.getGaClientId(),
          tracking_parameters: this.utmData,
        },
      });
    },

    updateCartFlag(value) {
      if (!this.account || typeof value !== 'number') return;
      this.account.activeCartTicketsCount = value;
    },

    setUtmData() {
      const route = useRoute();
      const isExceptionRoute = !!utmExceptionRoutes.find((exceptionRoute) =>
        route.fullPath.includes(exceptionRoute),
      );

      if (isExceptionRoute || !route.query) return;
      this.utmData = route.query;
    },
  },
});
